import { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Person as PersonIcon,
  AccountCircle as AccountCircleIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
  LocationOn as LocationOnIcon,
  Folder as FolderIcon,
  Photo as PhotoIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { collection, query, orderBy, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { format } from 'date-fns';

function MembershipManagement() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const q = query(collection(db, 'membership_applications'), orderBy('timestamp', 'desc'));
      const querySnapshot = await getDocs(q);
      const applicationsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate()
      }));
      setApplications(applicationsData);
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (application) => {
    setSelectedApplication(application);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedApplication(null);
  };

  const handleUpdateStatus = async (applicationId, newStatus) => {
    try {
      const applicationRef = doc(db, 'membership_applications', applicationId);
      await updateDoc(applicationRef, {
        status: newStatus,
        updatedAt: new Date()
      });
      
      // Update local state
      setApplications(prevApplications =>
        prevApplications.map(app =>
          app.id === applicationId ? { ...app, status: newStatus } : app
        )
      );
      
      handleCloseDialog();
    } catch (error) {
      console.error('Error updating application status:', error);
    }
  };

  const getStatusChipColor = (status) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    // If it's a Firebase timestamp
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      return new Date(timestamp.toDate()).toLocaleString();
    }
    
    // If it's a regular date string or timestamp
    try {
      return new Date(timestamp).toLocaleString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const ApplicationDetailsDialog = ({ open, application = {}, onClose, onApprove, onReject }) => {
    if (!open) return null;

    return (

      
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ borderBottom: '1px solid #e0e0e0', color: '#580100' }}>
          <PersonIcon /> Application Details
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={4}>
            {/* Left Column */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, bgcolor: '#fafafa', borderRadius: 2 }}>
                <Typography variant="h6" color="primary" gutterBottom sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: 1,
                  fontWeight: 'medium'
                }}>
                  <AccountCircleIcon /> Personal Information
                </Typography>
                
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <PersonIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Name"
                      secondary={application?.name || 'N/A'}
                      primaryTypographyProps={{ color: 'text.secondary', variant: 'body2' }}
                      secondaryTypographyProps={{ color: 'text.primary', fontWeight: 'medium' }}
                    />
                  </ListItem>

                  <ListItem>
                    <ListItemIcon>
                      <EmailIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Email"
                      secondary={application?.email || 'N/A'}
                      primaryTypographyProps={{ color: 'text.secondary', variant: 'body2' }}
                      secondaryTypographyProps={{ color: 'text.primary', fontWeight: 'medium' }}
                    />
                  </ListItem>

                  <ListItem>
                    <ListItemIcon>
                      <PhoneIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Mobile"
                      secondary={application?.mobile || 'N/A'}
                      primaryTypographyProps={{ color: 'text.secondary', variant: 'body2' }}
                      secondaryTypographyProps={{ color: 'text.primary', fontWeight: 'medium' }}
                    />
                  </ListItem>

                  <ListItem>
                    <ListItemIcon>
                      <BadgeIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Bar Council ID"
                      secondary={application?.barCouncilId || 'N/A'}
                      primaryTypographyProps={{ color: 'text.secondary', variant: 'body2' }}
                      secondaryTypographyProps={{ color: 'text.primary', fontWeight: 'medium' }}
                    />
                  </ListItem>
                </List>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" color="primary" gutterBottom sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    gap: 1,
                    fontWeight: 'medium'
                  }}>
                    <LocationOnIcon /> Addresses
                  </Typography>
                  
                  <Box sx={{ pl: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Residential Address
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ fontWeight: 'medium' }}>
                      {application?.residentialAddress || 'N/A'}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Office Address
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                      {application?.officeAddress || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            {/* Right Column */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, bgcolor: '#fafafa', borderRadius: 2 }}>
                <Typography variant="h6" color="primary" gutterBottom sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: 1,
                  fontWeight: 'medium'
                }}>
                  <FolderIcon /> Documents
                </Typography>

                <List>
                  {application?.photoUrl && (
                    <ListItem disablePadding sx={{ mb: 2 }}>
                      <Button
                        variant="outlined"
                        startIcon={<PhotoIcon />}
                        onClick={() => window.open(application.photoUrl)}
                        fullWidth
                        sx={{ 
                          justifyContent: 'flex-start',
                          borderColor: '#580100',
                          color: '#580100',
                          '&:hover': {
                            borderColor: '#400000',
                            bgcolor: 'rgba(88, 1, 0, 0.04)'
                          }
                        }}
                      >
                        View Photo
                      </Button>
                    </ListItem>
                  )}

                  {application?.certificateUrl && (
                    <ListItem disablePadding sx={{ mb: 2 }}>
                      <Button
                        variant="outlined"
                        startIcon={<InsertDriveFileIcon />}
                        onClick={() => window.open(application.certificateUrl)}
                        fullWidth
                        sx={{ 
                          justifyContent: 'flex-start',
                          borderColor: '#580100',
                          color: '#580100',
                          '&:hover': {
                            borderColor: '#400000',
                            bgcolor: 'rgba(88, 1, 0, 0.04)'
                          }
                        }}
                      >
                        View Bar Council Certificate
                      </Button>
                    </ListItem>
                  )}

                  {application?.paymentUrl && (
                    <ListItem disablePadding>
                      <Button
                        variant="outlined"
                        startIcon={<InsertDriveFileIcon />}
                        onClick={() => window.open(application.paymentUrl)}
                        fullWidth
                        sx={{ 
                          justifyContent: 'flex-start',
                          borderColor: '#580100',
                          color: '#580100',
                          '&:hover': {
                            borderColor: '#400000',
                            bgcolor: 'rgba(88, 1, 0, 0.04)'
                          }
                        }}
                      >
                        View Payment Proof
                      </Button>
                    </ListItem>
                  )}
                </List>

                <Box sx={{ mt: 4 }}>
                  <Typography variant="h6" color="primary" gutterBottom sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    gap: 1,
                    fontWeight: 'medium'
                  }}>
                    <InfoIcon /> Application Status
                  </Typography>

                  <Box sx={{ pl: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Chip 
                        label={(application?.status || 'pending').toUpperCase()}
                        color={application?.status === 'approved' ? 'success' : 
                              application?.status === 'rejected' ? 'error' : 'warning'}
                        size="small"
                        sx={{ fontWeight: 'medium' }}
                      />
                    </Box>

                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Submitted on
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                      {formatTimestamp(application?.timestamp)}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ 
          mt: 2, 
          borderTop: '1px solid #e0e0e0', 
          pt: 2,
          px: 3
        }}>
          <Button 
            onClick={onClose}
            variant="outlined"
            startIcon={<CloseIcon />}
            sx={{ 
              borderColor: '#580100',
              color: '#580100',
              '&:hover': {
                borderColor: '#400000',
                bgcolor: 'rgba(88, 1, 0, 0.04)'
              }
            }}
          >
            Close
          </Button>
          <Button
            onClick={onReject}
            variant="outlined"
            color="error"
            startIcon={<BlockIcon />}
            sx={{ ml: 1 }}
            disabled={application?.status !== 'pending'}
          >
            Reject
          </Button>
          <Button
            onClick={onApprove}
            variant="contained"
            startIcon={<CheckCircleIcon />}
            sx={{
              bgcolor: '#580100',
              '&:hover': {
                bgcolor: '#400000'
              }
            }}
            disabled={application?.status !== 'pending'}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      flexGrow: 1, 
      p: { xs: 2, md: 3 },
      ml: { xs: 0, md: '240px' } // Add left margin for sidebar on desktop
    }}>
      <Container maxWidth="lg">
        <Typography 
          variant="h4" 
          component="h1" 
          gutterBottom 
          sx={{ 
            color: '#580100',
            fontWeight: 600
          }}
        >
          Membership Applications
        </Typography>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell>Name</TableCell>
                <TableCell>Contact Info</TableCell>
                <TableCell>Bar Council ID</TableCell>
                <TableCell>Submission Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {applications.map((application) => (
                <TableRow key={application.id}>
                  <TableCell>{application.name}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{application.email}</Typography>
                    <Typography variant="body2">{application.mobile}</Typography>
                  </TableCell>
                  <TableCell>{application.barCouncilId}</TableCell>
                  <TableCell>
                    {application.timestamp ? 
                      format(application.timestamp, 'MMM dd, yyyy hh:mm a') : 
                      'N/A'
                    }
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={application.status || 'pending'}
                      color={getStatusChipColor(application.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleViewDetails(application)}
                      sx={{ 
                        borderColor: '#580100',
                        color: '#580100',
                        '&:hover': {
                          borderColor: '#400000',
                          bgcolor: 'rgba(88,1,0,0.05)'
                        }
                      }}
                    >
                      View Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <ApplicationDetailsDialog 
          open={openDialog} 
          application={selectedApplication || {}} 
          onClose={handleCloseDialog} 
          onApprove={() => handleUpdateStatus(selectedApplication?.id, 'approved')} 
          onReject={() => handleUpdateStatus(selectedApplication?.id, 'rejected')} 
        />
      </Container>
    </Box>
  );
}

export default MembershipManagement;
